/** Display helpers for the staff Events pages. Dates arrive as YYYY-MM-DD, times as HH:MM[:SS]. */

export const MODE_LABEL: Record<string, string> = {
  attendance: "Attendance",
  rsvp: "RSVP",
  rsvp_and_attendance: "RSVP, then attendance",
};

const TYPE_LABEL: Record<string, string> = {
  appointment: "Appointment",
  conference: "Conference",
  volunteer: "Volunteer",
  assembly: "Assembly",
  other: "Other",
};

export const EVENT_TYPES = Object.keys(TYPE_LABEL);

export function typeLabel(type: string | null | undefined) {
  if (!type) return "Event";
  return TYPE_LABEL[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

export function modeLabel(mode: string) {
  return MODE_LABEL[mode] || mode;
}

function parts(date: string | null | undefined) {
  const raw = date ? String(date).slice(0, 10) : "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [y, m, d] = raw.split("-").map(Number);
  return new Date(y, m - 1, d);
}

export function dayKey(date: string | null | undefined) {
  const raw = date ? String(date).slice(0, 10) : "";
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : "";
}

export function todayKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function formatTime(time: string | null | undefined) {
  if (!time) return "";
  const [h, m] = String(time).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return "";
  const d = new Date(2000, 0, 1, h, m);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

/** "Tue, Oct 27" */
export function shortDay(date: string | null | undefined) {
  const d = parts(date);
  return d ? d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" }) : "";
}

/** "Tuesday, October 27, 2026" */
export function longDay(date: string | null | undefined) {
  const d = parts(date);
  return d
    ? d.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    : "";
}

/** "October 2026" */
export function monthLabel(date: string | null | undefined) {
  const d = parts(date);
  return d ? d.toLocaleDateString([], { month: "long", year: "numeric" }) : "No date";
}

type Timed = {
  start_date: string | null;
  start_time: string | null;
  end_date?: string | null;
  end_time?: string | null;
};

/** "9:00 AM – 9:30 AM", "Starts 9:00 AM", or "Ends Nov 8, 5:00 PM" when it runs past the start day. */
export function timeRange(event: Timed) {
  const start = formatTime(event.start_time);
  const end = formatTime(event.end_time);
  const startDay = dayKey(event.start_date);
  const endDay = dayKey(event.end_date);
  if (endDay && endDay !== startDay) {
    const tail = [shortDay(endDay), end].filter(Boolean).join(", ");
    return [start, `ends ${tail}`].filter(Boolean).join(" · ");
  }
  if (start && end) return `${start} – ${end}`;
  return start;
}

/** Whole "when" line for the detail header. */
export function whenLong(event: Timed) {
  const day = longDay(event.start_date);
  const time = timeRange(event);
  if (!day) return time || "No date set";
  return [day, time].filter(Boolean).join(" · ");
}
